import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import courseService from '../services/courseService';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Set up PDF.js worker - use local file from public folder
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const CourseContent = ({ content, userHasAccess = false }) => {
  const { isAuthenticated } = useAuth();
  const [expandedSections, setExpandedSections] = useState([]);
  const [showAllSections, setShowAllSections] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState('1');
  const [totalPages, setTotalPages] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isManualNavigation, setIsManualNavigation] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const INITIAL_DISPLAY_COUNT = 10;

  // Prevent background scroll when PDF is open
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Prevent Ctrl+P (print), Ctrl+S (save), Ctrl+C (copy)
      // Also prevent Edge Web Capture (Ctrl+Shift+S) and other Edge shortcuts
      if (
        (e.ctrlKey && (e.key === 'p' || e.key === 'P')) ||
        (e.ctrlKey && (e.key === 's' || e.key === 'S')) ||
        (e.ctrlKey && (e.key === 'c' || e.key === 'C')) ||
        (e.ctrlKey && e.shiftKey && (e.key === 's' || e.key === 'S')) || // Edge Web Capture
        (e.ctrlKey && e.shiftKey && (e.key === 'x' || e.key === 'X')) || // Edge Collections
        (e.metaKey && (e.key === 'p' || e.key === 'P')) ||
        (e.metaKey && (e.key === 's' || e.key === 'S')) ||
        (e.metaKey && (e.key === 'c' || e.key === 'C')) ||
        (e.metaKey && e.shiftKey && (e.key === 's' || e.key === 'S'))
      ) {
        e.preventDefault();
        alert('This action is disabled for content protection.');
        return false;
      }
    };

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    if (selectedPdf) {
      document.body.style.overflow = 'hidden';

      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('fullscreenchange', handleFullscreenChange);

      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [selectedPdf]);

  // Sync pageInputValue with currentPage when it changes from scrolling
  useEffect(() => {
    setPageInputValue(currentPage.toString());
  }, [currentPage]);

  // Listen for session invalidation and close PDF viewer
  useEffect(() => {
    const handleSessionInvalidated = () => {
      // Close PDF viewer immediately when session is invalidated
      if (selectedPdf) {
        closePdfViewer();
      }
    };

    window.addEventListener('session-invalidated', handleSessionInvalidated);

    return () => {
      window.removeEventListener('session-invalidated', handleSessionInvalidated);
    };
  }, [selectedPdf]);

  // Auto-update current page based on scroll position
  useEffect(() => {
    if (!selectedPdf || totalPages === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Skip auto-update during manual navigation
        if (isManualNavigation) return;

        // Find the most visible page (highest intersection ratio)
        let mostVisibleEntry = null;
        let highestRatio = 0;

        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > highestRatio) {
            highestRatio = entry.intersectionRatio;
            mostVisibleEntry = entry;
          }
        });

        // Update current page to the most visible one
        if (mostVisibleEntry && highestRatio >= 0.3) {
          const pageNum = parseInt(mostVisibleEntry.target.id.replace('pdf-page-', ''));
          setCurrentPage(pageNum);
        }
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
        rootMargin: '0px'
      }
    );

    // Observe all page elements
    const pageElements = document.querySelectorAll('[id^="pdf-page-"]');
    pageElements.forEach((element) => observer.observe(element));

    return () => {
      pageElements.forEach((element) => observer.unobserve(element));
    };
  }, [selectedPdf, totalPages, isManualNavigation]);

  if (!content || content.length === 0) {
    return null;
  }

  const toggleSection = (sectionId) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const getTotalLessons = () => {
    return content.reduce((total, section) => total + (section.lessons?.length || 0), 0);
  };

  /**
   * Check if a lesson is locked (not accessible to the user)
   * A lesson is locked if it's not a preview AND the user doesn't have approved enrollment
   */
  const isLessonLocked = (lesson) => {
    // Preview lessons are always accessible
    if (lesson.is_preview) {
      return false;
    }
    // Non-preview lessons require approved enrollment
    return !userHasAccess;
  };

  /**
   * Handle lesson click - open PDF viewer
   * PDFs are served through backend protected endpoint
   */
  const handleLessonClick = (lesson) => {
    // Prevent clicking on locked lessons
    if (isLessonLocked(lesson)) {
      return;
    }

    if (lesson.pdf_file || lesson.id) {
      // Get protected PDF URL from backend
      const pdfUrl = courseService.getLessonPdfUrl(lesson.id);

      // Get JWT token for authentication
      const token = localStorage.getItem('access_token');

      setSelectedPdf({
        ...lesson,
        pdfUrl: pdfUrl,
        token: token
      });
      setCurrentPage(1);
      setScale(1.0);
      setTotalPages(0);
      setIsManualNavigation(false);

      // Close mobile sidebar when lesson is selected
      setIsMobileSidebarOpen(false);
    }
  };

  const closePdfViewer = () => {
    setSelectedPdf(null);
    setCurrentPage(1);
    setScale(1.0);
    setTotalPages(0);
    setIsManualNavigation(false);
    setIsMobileSidebarOpen(false);
  };

  /**
   * Handle PDF load error
   * Makes a test API request to check if session is still valid
   * If session was invalidated, the API interceptor will catch it and show the modal
   */
  const handlePdfLoadError = async (error) => {
    console.error('PDF load error:', error);

    // Make a test API request to check if session is still valid
    // If session was invalidated, the interceptor will catch it and show the modal
    try {
      await api.get('/auth/profile/');
    } catch (err) {
      // Error will be handled by the API interceptor
      // If it's a session_invalidated error, the modal will be shown automatically
      console.error('Session validation error:', err);
    }
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(prev => !prev);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.3));
  };

  const handlePageChange = (page) => {
    const pageNum = parseInt(page) || 1;
    if (pageNum >= 1 && pageNum <= totalPages) {
      // Disable auto-update during manual navigation
      setIsManualNavigation(true);
      setCurrentPage(pageNum);

      // Scroll to the selected page
      const pageElement = document.getElementById(`pdf-page-${pageNum}`);
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      // Re-enable auto-update after scroll completes (1 second delay for smooth scroll)
      setTimeout(() => {
        setIsManualNavigation(false);
      }, 1000);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setTotalPages(numPages);
    // Only set to page 1 if totalPages was 0 (initial load)
    // This prevents resetting the page on re-renders
    if (totalPages === 0) {
      setCurrentPage(1);
    }
  };

  // Get all lessons from all sections
  const getAllLessons = () => {
    const lessons = [];
    content.forEach(section => {
      if (section.lessons) {
        section.lessons.forEach(lesson => {
          lessons.push({ ...lesson, sectionTitle: section.title });
        });
      }
    });
    return lessons;
  };

  const toggleFullscreen = () => {
    const element = document.querySelector('.pdf-viewer-modal');

    if (!document.fullscreenElement) {
      element.requestFullscreen().catch((err) => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  // Prevent right-click
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  const allSectionsExpanded = () => {
    return content.every(section => expandedSections.includes(section.id));
  };

  const toggleAllSections = () => {
    if (allSectionsExpanded()) {
      setExpandedSections([]);
    } else {
      setExpandedSections(content.map(section => section.id));
    }
  };

  const visibleContent = showAllSections ? content : content.slice(0, INITIAL_DISPLAY_COUNT);
  const remainingSectionsCount = content.length - INITIAL_DISPLAY_COUNT;

  return (
    <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Course Content</h2>
          <p className="text-slate-600">
            {content.length} {content.length === 1 ? 'section' : 'sections'} • {getTotalLessons()} {getTotalLessons() === 1 ? 'lesson' : 'lessons'}
          </p>
        </div>
        <button
          onClick={toggleAllSections}
          className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
        >
          {allSectionsExpanded() ? 'Collapse All' : 'Expand All'}
        </button>
      </div>

      {/* PDF Viewer Overlay */}
      {selectedPdf && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          onContextMenu={handleContextMenu}
        >
          {/* Backdrop with blur */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={closePdfViewer}
          ></div>

          {/* PDF Viewer Modal */}
          <div className="pdf-viewer-modal relative z-10 w-full h-full sm:w-11/12 sm:h-5/6 bg-white sm:rounded-lg shadow-2xl overflow-hidden flex flex-col max-h-screen">
            {/* Header with Controls */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-2 sm:px-3 md:px-6 py-2 sm:py-3 flex items-center justify-between gap-1 flex-wrap sm:flex-nowrap">
              <div className="flex items-center gap-1 sm:gap-2 md:gap-3 flex-1 min-w-0 max-w-[50%] sm:max-w-none">
                {/* Mobile Menu Button */}
                <button
                  onClick={toggleMobileSidebar}
                  className="md:hidden text-white hover:text-blue-200 transition-colors p-1"
                  title="Toggle Lessons"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>

                <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span className="font-semibold text-white text-xs sm:text-sm md:text-base lg:text-lg truncate">{selectedPdf.title}</span>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2 lg:gap-4 flex-shrink-0">
                {/* Zoom Controls - Hidden on mobile */}
                <div className="hidden sm:flex items-center gap-2 bg-white bg-opacity-20 rounded-lg px-2 md:px-3 py-1">
                  <button
                    onClick={handleZoomOut}
                    className="text-white hover:text-blue-200 transition-colors"
                    title="Zoom Out"
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                    </svg>
                  </button>
                  <span className="text-white font-medium min-w-[40px] md:min-w-[60px] text-center text-xs md:text-sm">{Math.round(scale * 100)}%</span>
                  <button
                    onClick={handleZoomIn}
                    className="text-white hover:text-blue-200 transition-colors"
                    title="Zoom In"
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                    </svg>
                  </button>
                </div>

                {/* Page Navigation */}
                <div className="flex items-center gap-0.5 sm:gap-1 bg-white bg-opacity-20 rounded-lg px-1 sm:px-2 md:px-3 py-0.5 sm:py-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="text-white hover:text-blue-200 transition-colors disabled:opacity-50 p-0.5 sm:p-1"
                    disabled={currentPage === 1}
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <div className="flex items-center gap-0.5 sm:gap-1">
                    <input
                      type="number"
                      value={pageInputValue}
                      onChange={(e) => setPageInputValue(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handlePageChange(pageInputValue);
                          e.target.blur();
                        }
                      }}
                      onBlur={() => handlePageChange(pageInputValue)}
                      className="w-8 sm:w-10 md:w-12 lg:w-14 px-0.5 sm:px-1 md:px-2 py-0.5 sm:py-1 text-center bg-white text-slate-900 rounded border-none focus:ring-2 focus:ring-blue-300 text-xs md:text-sm"
                      min="1"
                      max={totalPages}
                    />
                    <span className="text-white text-xs md:text-sm whitespace-nowrap">/ {totalPages || '...'}</span>
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="text-white hover:text-blue-200 transition-colors disabled:opacity-50 p-0.5 sm:p-1"
                    disabled={currentPage === totalPages}
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Fullscreen Button - Hidden on very small mobile */}
                <button
                  onClick={toggleFullscreen}
                  className="hidden sm:flex text-white hover:text-blue-200 transition-colors px-1.5 sm:px-2 md:px-3 py-1 sm:py-1.5 md:py-2 bg-white bg-opacity-20 rounded-lg"
                  title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                >
                  {isFullscreen ? (
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  )}
                </button>

                {/* Close Button */}
                <button
                  onClick={closePdfViewer}
                  className="flex items-center gap-0.5 sm:gap-1 md:gap-2 px-1.5 sm:px-2 md:px-3 lg:px-4 py-1 sm:py-1.5 md:py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
                >
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="hidden sm:inline text-xs md:text-sm">Close</span>
                </button>
              </div>
            </div>

            {/* Main Content Area with Sidebar */}
            <div className="flex-1 flex overflow-hidden relative">
              {/* Mobile Sidebar Backdrop */}
              {isMobileSidebarOpen && (
                <div
                  className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-20"
                  onClick={() => setIsMobileSidebarOpen(false)}
                ></div>
              )}

              {/* Left Sidebar - Module List */}
              <div className={`
                ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
                md:translate-x-0
                fixed md:relative
                left-0 top-0 bottom-0
                w-64 sm:w-72 md:w-80
                bg-slate-50 border-r border-slate-200
                overflow-y-auto
                transition-transform duration-300 ease-in-out
                z-30 md:z-auto
                max-w-[85vw]
              `}>
                <div className="p-3 sm:p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base sm:text-lg font-semibold text-slate-900">Course Modules</h3>
                    {/* Close button for mobile */}
                    <button
                      onClick={() => setIsMobileSidebarOpen(false)}
                      className="md:hidden text-slate-500 hover:text-slate-700 p-1"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="space-y-2">
                    {getAllLessons().map((lesson) => {
                      const locked = isLessonLocked(lesson);
                      return (
                        <button
                          key={lesson.id}
                          onClick={() => handleLessonClick(lesson)}
                          disabled={locked}
                          className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 rounded-lg transition-colors ${
                            locked
                              ? 'bg-slate-100 border-l-4 border-slate-300 opacity-60 cursor-not-allowed'
                              : selectedPdf.id === lesson.id
                              ? 'bg-blue-100 border-l-4 border-blue-600'
                              : 'bg-white hover:bg-slate-100 border-l-4 border-transparent'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            {locked ? (
                              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="text-xs text-slate-500 mb-1 truncate">{lesson.sectionTitle}</div>
                              <div className={`text-xs sm:text-sm font-medium ${locked ? 'text-slate-500' : 'text-slate-900'} line-clamp-2`}>
                                {lesson.title}
                              </div>
                              {lesson.is_preview && !userHasAccess && (
                                <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                                  Preview
                                </span>
                              )}
                              {locked && (
                                <span className="inline-block mt-1 px-2 py-0.5 bg-slate-200 text-slate-600 text-xs font-semibold rounded">
                                  Locked
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* PDF Content */}
              <div
                className="flex-1 bg-gray-100 overflow-y-auto overflow-x-hidden relative"
                style={{
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  maxHeight: '100%'
                }}
                onContextMenu={(e) => e.preventDefault()}
              >
                <div className="flex flex-col items-center p-2 sm:p-4 gap-2 sm:gap-4">
                  <Document
                    file={{
                      url: selectedPdf.pdfUrl,
                      httpHeaders: selectedPdf.token ? {
                        'Authorization': `Bearer ${selectedPdf.token}`
                      } : {}
                    }}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={handlePdfLoadError}
                    loading={
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-slate-600">Loading PDF...</p>
                      </div>
                    }
                    error={
                      <div className="text-center py-8">
                        {!isAuthenticated && selectedPdf?.is_preview ? (
                          <div>
                            <p className="text-blue-600 font-semibold mb-2">Register to view preview</p>
                            <p className="text-slate-600 text-sm">Please create an account to access preview lessons</p>
                            <a
                              href="/register"
                              className="inline-block mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              Register Now
                            </a>
                          </div>
                        ) : (
                          <p className="text-red-600">Failed to load PDF</p>
                        )}
                      </div>
                    }
                  >
                    {/* Render all pages for continuous scrolling */}
                    {Array.from(new Array(totalPages), (el, index) => (
                      <div
                        key={`page_${index + 1}`}
                        id={`pdf-page-${index + 1}`}
                        className="relative shadow-lg mb-2 sm:mb-4 w-full max-w-full"
                      >
                        <Page
                          pageNumber={index + 1}
                          scale={scale}
                          width={typeof window !== 'undefined' ? Math.min(window.innerWidth - 32, 800 * scale) : undefined}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          className="max-w-full h-auto"
                        />

                        {/* Watermark overlay on each page */}
                        <div
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            userSelect: 'none',
                            zIndex: 10
                          }}
                        >
                          {/* Multiple watermarks */}
                          <div className="absolute top-1/4 left-1/4 transform -rotate-45 text-blue-500 opacity-20 select-none font-bold text-lg sm:text-2xl md:text-3xl">
                            LEARNERS EDGE
                          </div>
                          <div className="absolute top-2/3 left-2/3 transform -rotate-45 text-blue-500 opacity-20 select-none font-bold text-lg sm:text-2xl md:text-3xl">
                            LEARNERS EDGE
                          </div>
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -rotate-45 text-blue-500 opacity-15 select-none font-bold text-xl sm:text-3xl md:text-4xl">
                            PREVIEW ONLY
                          </div>
                        </div>

                        {/* Page number indicator */}
                        <div className="absolute bottom-2 right-2 bg-blue-600 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium">
                          Page {index + 1}
                        </div>
                      </div>
                    ))}
                  </Document>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {visibleContent.map((section) => (
          <div key={section.id} className="border border-slate-200 rounded-lg overflow-hidden">
            {/* Section Header */}
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full px-4 py-2 bg-slate-50 hover:bg-slate-100 transition-colors flex items-center justify-between text-left"
            >
              <div className="flex items-center gap-2">
                <svg
                  className={`w-4 h-4 text-slate-600 transition-transform ${
                    expandedSections.includes(section.id) ? 'rotate-90' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">{section.title}</h3>
                  <p className="text-xs text-slate-600 mt-0.5">
                    {section.lessons?.length || 0} lessons
                  </p>
                </div>
              </div>
            </button>

            {/* Section Lessons */}
            {expandedSections.includes(section.id) && (
              <div className="bg-white">
                {section.lessons?.map((lesson) => {
                  const locked = isLessonLocked(lesson);
                  return (
                    <div
                      key={lesson.id}
                      onClick={() => handleLessonClick(lesson)}
                      className={`px-4 py-2 border-t border-slate-100 flex items-center justify-between transition-colors ${
                        locked
                          ? 'opacity-60 cursor-not-allowed'
                          : (lesson.pdf_file || lesson.id)
                          ? 'cursor-pointer hover:bg-slate-50'
                          : ''
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {/* Icon - Lock or PDF */}
                        {locked ? (
                          <svg
                            className="w-4 h-4 text-slate-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4 text-red-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                            />
                          </svg>
                        )}
                        <span className={`text-sm ${locked ? 'text-slate-500' : 'text-slate-700'}`}>
                          {lesson.title}
                        </span>
                        {lesson.is_preview && !userHasAccess && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                            Preview
                          </span>
                        )}
                        {locked && (
                          <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs font-semibold rounded">
                            Locked
                          </span>
                        )}
                      </div>
                      {!locked && (lesson.pdf_file || lesson.id) && (
                        <svg
                          className="w-4 h-4 text-slate-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {/* Show More Sections Button */}
        {!showAllSections && remainingSectionsCount > 0 && (
          <div className="mt-6 pt-6 border-t-2 border-slate-300">
            <button
              onClick={() => setShowAllSections(true)}
              className="w-full px-6 py-4 border-2 border-blue-500 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-3 text-blue-700 font-semibold"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              <span className="text-base">Show {remainingSectionsCount} more {remainingSectionsCount === 1 ? 'section' : 'sections'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseContent;
